import { ChangeDetectionStrategy, Component, inject, input, type InputSignal } from '@angular/core';
import { ReactiveFormsModule, type FormArray, type FormGroup } from '@angular/forms';
import { type ICountryPhoneMetaDto } from '@pecan/api';
import { MatButton } from '@angular/material/button';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { TranslatePipe } from '@ngx-translate/core';
import type { IPhoneFormGroup } from '../../models/phone-form-group.interface';
import type { IPhoneInfoFormValue } from '../../models/phone-info-form-value.interface';
import { PhoneFormService } from '../../services/phone-form.service';
import { phoneInfoRequiredValidator } from '../../validators/phone-info-required.validator';
import { PhoneInfoComponent } from '../phone-info/phone-info.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, TranslatePipe, MatRadioGroup, MatRadioButton, MatButton, PhoneInfoComponent],
    selector: 'app-phones-info',
    styleUrls: ['./phones-info.component.scss'],
    templateUrl: './phones-info.component.html',
})
export class PhonesInfoComponent {
    public readonly phoneGroups: InputSignal<FormArray<FormGroup<IPhoneFormGroup>>> =
        input.required<FormArray<FormGroup<IPhoneFormGroup>>>();

    public readonly country: InputSignal<ICountryPhoneMetaDto | null> = input<ICountryPhoneMetaDto | null>(null);

    private _phoneFormService: PhoneFormService = inject(PhoneFormService);

    public add(): void {
        const group = this._createPhoneGroup();

        this.phoneGroups().push(group);
    }

    public removePhone(i: number): void {
        this.phoneGroups().removeAt(i);
    }

    public onChangePrimary(i: number): void {
        const primaryControl = this.phoneGroups().controls[i].get('isPrimary');

        if (!primaryControl) {
            throw new Error('isPrimary field is required but was not found.');
        }

        this.phoneGroups().controls.forEach((group: FormGroup) => group.controls.isPrimary.setValue(false, { emitEvent: false }));

        primaryControl.setValue(true, { emitEvent: false });

        this._updatePhoneValidation();
    }

    private _createPhoneGroup(): FormGroup {
        const group = this._phoneFormService.createFormGroup({ isPhoneRequired: false });

        const country = this.country();

        if (country) {
            const phoneInfo = group.controls.phoneInfo;
            const current: IPhoneInfoFormValue = phoneInfo.value;
            phoneInfo.setValue({ ...current, locale: { isoCode: country.isoCode, countryCode: country.countryCode } });
        }

        return group;
    }

    private _updatePhoneValidation(): void {
        this.phoneGroups().controls.forEach((group: FormGroup) => {
            const phoneInfo = group.controls.phoneInfo;
            const isRequired = group.controls.isPrimary.value || group.controls.phoneInfo.value?.phone || group.controls.ext.value;

            phoneInfo.setValidators(isRequired ? [phoneInfoRequiredValidator] : []);
            phoneInfo.updateValueAndValidity();
        });
    }
}
